import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildMarketplaceCategoryLocation,
  getMarketplaceRequestKeyword,
  isMarketplaceListPath,
  MARKETPLACE_CATEGORY_ALL,
  MARKETPLACE_KEYWORD_MIN_LENGTH,
  normalizeMarketplaceKeyword,
} from '../../utils/marketplace/marketplaceKeywordSearch';
import {
  formatOmnibarActiveFilterChip,
  getOmnibarOperator,
  isOmnibarDrilldownOperator,
  parseOmnibarQuery,
  splitOmnibarOperatorDraft,
} from '../../utils/navigation/dashboardOmnibarFilters';
import { loadDashboardOmnibarIndex } from '../../utils/navigation/dashboardOmnibarIndex';
import {
  getCategoryDrilldownSuggestions,
  getEmptyOmnibarSuggestions,
  getTypedOmnibarSuggestions,
} from '../../utils/navigation/dashboardOmnibarSuggestions';
import useHideCreatorMapEntryPoints from '../navigation/useHideCreatorMapEntryPoints';
import { CREATOR_MAP_PATH } from '../../utils/navigation/creatorMapVisibility';
import { buildAdminUsersSearchLocation } from '../../utils/navigation/adminOmnibarDestinations';

const emptyIndex = () => ({
  destinations: [],
  entities: [],
  marketplacePath: '/marketplace/influencers',
});

/**
 * Dashboard Navbar omnibar: personal entities + destinations, marketplace fallback.
 * Prefetches the searchable index when the panel opens (not on Navbar mount).
 */
function useDashboardOmnibarSearch(userType) {
  const navigate = useNavigate();
  const location = useLocation();
  const hideCreatorMapEntryPoints = useHideCreatorMapEntryPoints();
  const [draft, setDraftState] = useState('');
  const [categoryMode, setCategoryMode] = useState(null);
  const [index, setIndex] = useState(emptyIndex);
  const [indexLoading, setIndexLoading] = useState(false);
  const loadPromiseRef = useRef(null);

  const setDraft = useCallback((next) => {
    setDraftState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      return value;
    });
  }, []);

  const visibleIndex = useMemo(() => {
    if (!hideCreatorMapEntryPoints) return index;
    return {
      ...index,
      destinations: (index.destinations || []).filter(
        (row) => row.href !== CREATOR_MAP_PATH,
      ),
    };
  }, [hideCreatorMapEntryPoints, index]);

  const parsedDraft = useMemo(
    () => parseOmnibarQuery(draft, userType),
    [draft, userType],
  );

  const activeOperator = parsedDraft.operator;
  const activeOperatorMeta = useMemo(
    () => (activeOperator ? getOmnibarOperator(userType, activeOperator) : null),
    [activeOperator, userType],
  );
  const activeFilterChipLabel = useMemo(
    () => formatOmnibarActiveFilterChip(parsedDraft, userType),
    [parsedDraft, userType],
  );

  const inputDisplayValue = useMemo(() => {
    const parts = splitOmnibarOperatorDraft(draft, userType);
    if (!parts) return draft;
    return parts.valuePart.replace(/^\s/, '');
  }, [draft, userType]);

  const promptPlaceholder = useMemo(() => {
    if (categoryMode) {
      const op = getOmnibarOperator(userType, categoryMode);
      return op?.promptPlaceholder || 'Search';
    }
    if (activeOperatorMeta?.promptPlaceholder) {
      return activeOperatorMeta.promptPlaceholder;
    }
    return null;
  }, [activeOperatorMeta, categoryMode, userType]);

  const navigateTo = useCallback((next) => {
    if (!next?.pathname) return false;
    const nextSearch = next.search || '';
    if (next.pathname === location.pathname && nextSearch === location.search) {
      return false;
    }
    window.scrollTo(0, 0);
    const enteringMarketplaceList = (
      isMarketplaceListPath(next.pathname)
      && !isMarketplaceListPath(location.pathname)
    );
    if (enteringMarketplaceList) {
      window.location.assign(`${next.pathname}${nextSearch}`);
      return true;
    }
    // Marketplace detail pages (brand/campaign) also leave the dashboard shell.
    if (
      next.pathname.startsWith('/marketplace/')
      || next.pathname.startsWith('/creator-map')
    ) {
      window.location.assign(`${next.pathname}${nextSearch}`);
      return true;
    }
    navigate({
      pathname: next.pathname,
      search: nextSearch,
    });
    return true;
  }, [location.pathname, location.search, navigate]);

  const ensureIndex = useCallback(async () => {
    if (loadPromiseRef.current) {
      return loadPromiseRef.current;
    }
    setIndexLoading(true);
    loadPromiseRef.current = loadDashboardOmnibarIndex(userType)
      .then((next) => {
        setIndex(next);
        return next;
      })
      .catch(() => {
        const fallback = emptyIndex();
        fallback.marketplacePath = userType === 'influencer'
          ? '/marketplace/campaigns'
          : '/marketplace/influencers';
        setIndex(fallback);
        return fallback;
      })
      .finally(() => {
        setIndexLoading(false);
        loadPromiseRef.current = null;
      });
    return loadPromiseRef.current;
  }, [userType]);

  const getSuggestions = useCallback((query, open) => {
    if (!open) return [];
    const trimmed = String(query || '').trim();
    const parsed = parseOmnibarQuery(trimmed, userType);

    if (categoryMode && !parsed.operator) {
      return getCategoryDrilldownSuggestions(
        categoryMode,
        userType,
        visibleIndex,
        trimmed,
      );
    }

    if (!trimmed) {
      return getEmptyOmnibarSuggestions(userType);
    }
    return getTypedOmnibarSuggestions(trimmed, userType, visibleIndex);
  }, [categoryMode, visibleIndex, userType]);

  const selectSuggestion = useCallback((item) => {
    if (!item) return { navigated: false, reason: 'empty' };

    if (item.kind === 'filter-category') {
      if (item.drilldown || isOmnibarDrilldownOperator(item.operatorKey)) {
        setCategoryMode(item.operatorKey);
        setDraftState('');
        return { navigated: false, reason: 'drilldown' };
      }
      setCategoryMode(null);
      setDraftState(item.insertText || `${item.operatorKey}:`);
      return { navigated: false, reason: 'insert' };
    }

    if (item.kind === 'filter' || item.kind === 'filter-completion') {
      setCategoryMode(null);
      setDraftState(item.insertText || item.label || '');
      return { navigated: false, reason: 'insert' };
    }

    if (item.kind === 'marketplace') {
      const keyword = getMarketplaceRequestKeyword(item.keyword || draft);
      if (!keyword) {
        return { navigated: false, reason: 'too-short' };
      }
      setCategoryMode(null);
      if (userType === 'admin') {
        const navigated = navigateTo(buildAdminUsersSearchLocation(keyword));
        return { navigated, reason: 'users' };
      }
      const navigated = navigateTo(buildMarketplaceCategoryLocation({
        pathname: location.pathname,
        search: location.search,
        userType,
        category: MARKETPLACE_CATEGORY_ALL,
        keyword,
      }));
      return { navigated, reason: 'marketplace' };
    }

    if (item.kind === 'marketplace-browse') {
      setCategoryMode(null);
      if (userType === 'admin') {
        const navigated = navigateTo(buildAdminUsersSearchLocation(''));
        return { navigated, reason: 'browse' };
      }
      const navigated = navigateTo(buildMarketplaceCategoryLocation({
        pathname: location.pathname,
        search: location.search,
        userType,
        category: MARKETPLACE_CATEGORY_ALL,
        keyword: '',
      }));
      return { navigated, reason: 'browse' };
    }

    if (item.href) {
      setCategoryMode(null);
      let pathname = item.href;
      let search = '';
      const qIndex = item.href.indexOf('?');
      if (qIndex >= 0) {
        pathname = item.href.slice(0, qIndex);
        search = item.href.slice(qIndex);
      }
      const navigated = navigateTo({ pathname, search });
      return { navigated, reason: item.kind || 'navigate' };
    }

    return { navigated: false, reason: 'noop' };
  }, [draft, location.pathname, location.search, navigateTo, userType]);

  const setDraftFromInput = useCallback((visibleValue) => {
    const nextVisible = String(visibleValue ?? '');
    const parts = splitOmnibarOperatorDraft(draft, userType);
    if (parts) {
      setDraftState(`${parts.operator}:${nextVisible}`);
      setCategoryMode(null);
      return;
    }
    setDraftState(nextVisible);
    if (nextVisible.trim()) {
      // Keep categoryMode while filtering drill-down picks with plain text.

    }
  }, [draft, userType]);

  const submit = useCallback(() => {
    const normalized = normalizeMarketplaceKeyword(draft);
    setDraftState(normalized);
    setCategoryMode(null);

    if (userType === 'admin') {
      if (!normalized) {
        const navigated = navigateTo(buildAdminUsersSearchLocation(''));
        return { navigated, reason: 'browse' };
      }
      const typed = getTypedOmnibarSuggestions(normalized, userType, visibleIndex);
      const directHit = typed.find((row) => (
        row.kind === 'destination' || row.kind === 'entity'
      ));
      if (directHit?.href) {
        return selectSuggestion(directHit);
      }
      const navigated = navigateTo(buildAdminUsersSearchLocation(normalized));
      return { navigated, reason: 'users' };
    }

    if (!normalized) {
      const navigated = navigateTo(buildMarketplaceCategoryLocation({
        pathname: location.pathname,
        search: location.search,
        userType,
        category: MARKETPLACE_CATEGORY_ALL,
        keyword: '',
      }));
      return { navigated, reason: 'browse' };
    }

    if (normalized.length < MARKETPLACE_KEYWORD_MIN_LENGTH) {
      return { navigated: false, reason: 'too-short' };
    }

    // Prefer first entity/destination match on Enter without highlight.
    const typed = getTypedOmnibarSuggestions(normalized, userType, visibleIndex);
    const directHit = typed.find((row) => (
      row.kind === 'destination' || row.kind === 'entity'
    ));
    if (directHit?.href) {
      return selectSuggestion(directHit);
    }

    const parsed = parseOmnibarQuery(normalized, userType);
    if (parsed.operator) {
      return { navigated: false, reason: 'no-match' };
    }

    const navigated = navigateTo(buildMarketplaceCategoryLocation({
      pathname: location.pathname,
      search: location.search,
      userType,
      category: MARKETPLACE_CATEGORY_ALL,
      keyword: normalized,
    }));
    return { navigated, reason: 'marketplace' };
  }, [
    draft,
    visibleIndex,
    location.pathname,
    location.search,
    navigateTo,
    selectSuggestion,
    userType,
  ]);

  const clear = useCallback(() => {
    setDraftState('');
    setCategoryMode(null);
  }, []);

  const clearActiveFilter = useCallback(() => {
    setDraftState('');
    setCategoryMode(null);
  }, []);

  const resetCategoryMode = useCallback(() => {
    setCategoryMode(null);
  }, []);

  return {
    draft,
    setDraft,
    setDraftFromInput,
    inputDisplayValue,
    categoryMode,
    activeOperator,
    activeFilterChipLabel,
    promptPlaceholder,
    index: visibleIndex,
    indexLoading,
    ensureIndex,
    getSuggestions,
    selectSuggestion,
    submit,
    clear,
    clearActiveFilter,
    resetCategoryMode,
  };
}

export default useDashboardOmnibarSearch;
